import { Injectable } from '@angular/core';

export type NestedNumberObject = {
  [key1: string]: {
    [key2: string]: number;
  };
};

@Injectable({
  providedIn: 'root',
})
export class StructureTransformer {

  public collectInnerValues<T>(
    values: { [key: string]: T; },
    distinct: boolean = false,
  ): T[] {

    if (!values) {
      return [];
    }

    const flatValues = Object.values(values);

    return distinct ? this.uniqueByValue(flatValues) : flatValues;
  }

  public collectInnerValues2D<T>(
    values: { [key1: string]: { [key2: string]: T; }; },
    distinct: boolean = false,
  ): { [key1: string]: T[]; } {

    if (!values) {
      return {};
    }

    const result: { [key1: string]: T[] } = {};

    for (const key1 in values) {
      const values2 = values[key1];

      result[key1] = result[key1] || [];

      const values3 = Object.values(values2);

      result[key1] = result[key1].concat(values3);

      if (distinct) {
        result[key1] = this.uniqueByValue(result[key1]);
      }
    }

    return result;
  }

  public mergeInnerArrays<T>(
    values: { [key: string]: T[]; },
    distinct: boolean = false,
  ): T[] {

    if (!values) {
      return [];
    }

    const flatValues = Object.values(values).flat();

    return distinct ? this.uniqueByValue(flatValues) : flatValues;
  }

  public mergeInnerArrays2D<T>(
    values: { [key1: string]: { [key2: string]: T[]; }; },
    distinct: boolean = false,
  ): { [key1: string]: T[]; } {

    if (!values) {
      return {};
    }

    const result: { [key1: string]: T[] } = {};

    for (const key1 in values) {
      const values2 = values[key1];

      result[key1] = result[key1] || [];

      for (const key2 in values2) {
        result[key1].push(...values2[key2]);
      }

      if (distinct) {
        result[key1] = this.uniqueByValue(result[key1]);
      }
    }

    return result;
  }

  public mergeInnerNumbers(
    accumulate: boolean,
    maxAccumulation: number | undefined,
    ...objects: NestedNumberObject[]
  ): NestedNumberObject {

    const result: NestedNumberObject = {};

    for (const obj of objects) {
      if (!obj) {
        continue;
      }

      for (const key1 in obj) {
        if (!obj.hasOwnProperty(key1)) {
          continue;
        }

        if (!result[key1]) {
          result[key1] = {};
        }

        for (const key2 in obj[key1]) {
          if (!obj[key1].hasOwnProperty(key2)) {
            continue;
          }

          if (accumulate && result[key1][key2] !== undefined) {

            const accumulation = result[key1][key2] + obj[key1][key2];

            result[key1][key2] = maxAccumulation !== undefined ? Math.min(accumulation, maxAccumulation) : accumulation;

          } else {
            result[key1][key2] = obj[key1][key2];
          }
        }
      }
    }

    return result;
  }

  private uniqueByValue<T>(
    values: T[],
  ): T[] {

    const uniqueMap = new Map(
      values.map(value => [JSON.stringify(value), value]),
    );

    return Array.from(uniqueMap.values());
  }

}
